/**
 * Exercise-template helpers.
 *
 * Templates are written to be read by a teacher, not a compiler. These builders
 * carry the boilerplate so that a template file shows the *mathematics* of a
 * problem and the wrong turns it is designed to catch.
 */

import type { Choice, Difficulty, ExerciseTemplate, Question, TemplateResult, Trap } from '@yuan-math/exercises';
import type { Fraction, Rng } from '@yuan-math/math';
import { toText } from '@yuan-math/math';
import type { Figure, Step } from '@yuan-math/curriculum';

export function template(config: {
  id: string;
  conceptId: string;
  description: string;
  difficulties: Difficulty[];
  build: ExerciseTemplate['build'];
}): ExerciseTemplate {
  return config;
}

export const question = (config: Question): Question => config;

/**
 * Renders a signed constant to append to an expression: `+ 5`, `- 5`, or —
 * for zero — nothing at all.
 *
 * Printing `x^2 - x + 0` is not a rounding infelicity; it tells the student
 * that a term is there when it is not, and it is the sort of thing that makes
 * generated content feel generated. Grading never notices, because the value is
 * correct either way.
 */
export const signedConstant = (value: number): string =>
  value === 0 ? '' : `${value < 0 ? '- ' : '+ '}${Math.abs(value)}`;

/**
 * Renders a signed term in a variable: `+ 5x`, `- x`, or nothing for zero.
 * A coefficient of 1 is dropped, because `1x` is not how anyone writes it.
 */
export const signedTerm = (value: number, variable = 'x'): string => {
  if (value === 0) return '';
  const magnitude = Math.abs(value) === 1 ? '' : String(Math.abs(value));
  return `${value < 0 ? '- ' : '+ '}${magnitude}${variable}`;
};

/**
 * Joins the pieces of an expression, dropping the gaps left by terms that
 * rendered to nothing.
 *
 * Without this, a vanished constant leaves `y = -3x^2 ` with a trailing space,
 * and two vanished terms leave a double one. Harmless to a parser and visible
 * to a reader.
 */
export const expr = (...parts: (string | number)[]): string =>
  parts.map(String).join(' ').replace(/\s+/g, ' ').trim();

/** Renders a leading coefficient: 1 and −1 lose the digit, 0 loses the term. */
export const leadingTerm = (value: number, variable = 'x'): string => {
  if (value === 0) return '0';
  if (value === 1) return variable;
  if (value === -1) return `-${variable}`;
  return `${value}${variable}`;
};

/**
 * A non-zero integer in [−limit, limit], avoiding the excluded values.
 *
 * Written because the obvious guard is wrong. `if (q === p) q = p + 1` looks
 * like it only enforces distinctness, and at p = −1 it quietly produces q = 0 —
 * which for a quadratic means a constant term of zero and a question printed as
 * `x² − x + 0`. Grading never caught it, because (x − 1)(x + 0) really is
 * equivalent to x² − x; the damage is entirely in what the student is shown.
 *
 * Excluding the negative of a chosen root as well keeps the middle term from
 * vanishing, which would print `x² + 0x − 9` for what is really a difference of
 * two squares.
 */
export function pickDistinct(rng: Rng, limit: number, exclude: readonly number[]): number {
  const candidates: number[] = [];
  for (let value = -limit; value <= limit; value += 1) {
    if (value !== 0 && !exclude.includes(value)) candidates.push(value);
  }
  // The caller's ranges always leave choices; the fallback keeps this total.
  return candidates.length > 0 ? (rng.pick(candidates) as number) : 1;
}

/**
 * A tolerance for an answer wanted to within `absolute`.
 *
 * `numbersMatch` scales its tolerance by the size of the numbers involved, so
 * a flat `tolerance: 0.05` does not mean "within 0.05" — it means "within 5%".
 * On the obtuse angle of an ambiguous triangle that is a band wide enough to
 * swallow the acute angle as well, and the trap naming the misconception is
 * stripped for grading as correct; on a sector area of 636 a flat 0.01 accepts
 * an answer six units out. Say the absolute figure and convert it here.
 *
 * The default of 1e-9 in the grader is an exact match, so a template that
 * wants one passes no tolerance at all rather than a small figure.
 */
export const within = (absolute: number, value: number): number =>
  absolute / Math.max(1, Math.abs(value));

/**
 * The same thing said in the units a question is actually written in: one unit
 * in the last place the student was asked to write. A question ending "give
 * your answer to two decimal places" wants `decimalTolerance(answer, 2)`.
 */
export const decimalTolerance = (value: number, places: number): number =>
  within(10 ** -places, value);

export const trap = (response: string | number, misconceptionId: string): Trap => ({
  response: String(response),
  misconceptionId,
});

/** A wrong answer produced by a named misunderstanding, as a fraction. */
export const fractionTrap = (value: Fraction, misconceptionId: string): Trap => ({
  response: toText(value),
  misconceptionId,
});

export function numericExercise(config: {
  prompt: string;
  latex?: string;
  figure?: Figure;
  unit?: string;
  value: number;
  tolerance?: number;
  summary: string;
  steps?: Step[];
  hint?: string;
  traps?: Trap[];
}): TemplateResult {
  return {
    type: 'numeric',
    question: {
      prompt: config.prompt,
      ...(config.latex ? { latex: config.latex } : {}),
      ...(config.figure ? { figure: config.figure } : {}),
      ...(config.unit ? { unit: config.unit } : {}),
    },
    answer: { kind: 'numeric', value: config.value, ...(config.tolerance ? { tolerance: config.tolerance } : {}) },
    explanation: {
      summary: config.summary,
      ...(config.steps ? { steps: config.steps } : {}),
      ...(config.hint ? { hint: config.hint } : {}),
    },
    ...(config.traps?.length ? { traps: config.traps } : {}),
  };
}

export function fractionExercise(config: {
  prompt: string;
  latex?: string;
  figure?: Figure;
  value: Fraction;
  requireSimplified?: boolean;
  requireExactForm?: boolean;
  summary: string;
  steps?: Step[];
  hint?: string;
  traps?: Trap[];
}): TemplateResult {
  return {
    type: 'fraction',
    question: {
      prompt: config.prompt,
      ...(config.latex ? { latex: config.latex } : {}),
      ...(config.figure ? { figure: config.figure } : {}),
    },
    answer: {
      kind: 'fraction',
      value: config.value,
      ...(config.requireSimplified ? { requireSimplified: true } : {}),
      ...(config.requireExactForm ? { requireExactForm: true } : {}),
    },
    explanation: {
      summary: config.summary,
      ...(config.steps ? { steps: config.steps } : {}),
      ...(config.hint ? { hint: config.hint } : {}),
    },
    ...(config.traps?.length ? { traps: config.traps } : {}),
  };
}

export function choiceExercise(config: {
  prompt: string;
  latex?: string;
  figure?: Figure;
  choices: Choice[];
  correctId: string;
  summary: string;
  steps?: Step[];
  hint?: string;
}): TemplateResult {
  return {
    type: 'multiple-choice',
    question: {
      prompt: config.prompt,
      ...(config.latex ? { latex: config.latex } : {}),
      ...(config.figure ? { figure: config.figure } : {}),
      choices: config.choices,
    },
    answer: { kind: 'choice', correctId: config.correctId },
    explanation: {
      summary: config.summary,
      ...(config.steps ? { steps: config.steps } : {}),
      ...(config.hint ? { hint: config.hint } : {}),
    },
  };
}

export function expressionExercise(config: {
  prompt: string;
  latex?: string;
  canonical: string;
  /** Reject an answer left as a sum. Set when factorising *is* the skill. */
  requireFactored?: boolean;
  summary: string;
  steps?: Step[];
  hint?: string;
  traps?: Trap[];
}): TemplateResult {
  return {
    type: 'expression',
    question: { prompt: config.prompt, ...(config.latex ? { latex: config.latex } : {}) },
    answer: {
      kind: 'expression',
      canonical: config.canonical,
      ...(config.requireFactored ? { requireFactored: true } : {}),
    },
    explanation: {
      summary: config.summary,
      ...(config.steps ? { steps: config.steps } : {}),
      ...(config.hint ? { hint: config.hint } : {}),
    },
    ...(config.traps?.length ? { traps: config.traps } : {}),
  };
}

/** Builds the four options of a multiple-choice item, shuffled deterministically. */
export function options(
  entries: { latex?: string; label?: string; misconceptionId?: string; correct?: boolean }[],
  shuffle: <T>(items: readonly T[]) => T[],
): { choices: Choice[]; correctId: string } {
  const withIds = entries.map((entry, index) => ({
    id: `c${index}`,
    ...(entry.latex ? { latex: entry.latex } : {}),
    ...(entry.label ? { label: entry.label } : {}),
    ...(entry.misconceptionId ? { misconceptionId: entry.misconceptionId } : {}),
    correct: entry.correct ?? false,
  }));
  const correctId = withIds.find((entry) => entry.correct)?.id ?? 'c0';
  const choices = shuffle(withIds).map(({ correct: _correct, ...choice }) => choice);
  return { choices, correctId };
}

/**
 * Drops any trap whose printed response repeats one already listed.
 *
 * Two traps on the same exercise that normalise to the same response are
 * indistinguishable: whichever is listed first wins, and the student is told
 * about an error they may not have made. Generated parameters make that easy to
 * author by accident — two different wrong procedures agreeing for one choice of
 * numbers — and it is caught by the content suite rather than by review, which
 * is a slow way to find it.
 *
 * This is a guard, not a substitute for choosing ranges. Where a collision means
 * the item has stopped diagnosing what it exists to diagnose, pin the parameter
 * instead and say so in a comment; where it is a genuine coincidence at one
 * value out of fifty, losing the second diagnosis there is the right trade.
 */
export const distinctTraps = (entries: Trap[]): Trap[] => {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    const key = entry.response.trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
